import { HttpErrorResponse } from '@angular/common/http';
import { Provider, inject } from '@angular/core';
import { REALTIME_TOKEN_PROVIDER, RealtimeTokenProvider } from '@zayan-erp/realtime';
import { UserAuthenticationService } from '@zayan-erp/administration';
import { lastValueFrom } from 'rxjs';
import { ErpSsoService } from '../authentication-module/erp-sso.service';

/** The API allows no clock skew and the hub closes the socket the moment its token expires. */
const RENEW_BEFORE_EXPIRY_MS = 60_000;

function expiresSoon(token: string): boolean {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' && exp * 1000 - Date.now() < RENEW_BEFORE_EXPIRY_MS;
  } catch {
    return false;
  }
}

/** No answer, or the API failing: the next reconnect attempt tries again. Any other failure ends the session. */
function apiUnreachable(error: unknown): boolean {
  return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
}

/**
 * Adapts this application's auth service to the realtime library's token contract.
 *
 * The adapter lives here, in the app, so the library imports nothing from the rest of the
 * workspace and the auth service is read without being modified. Swapping auth later changes
 * this one file.
 */
export const realtimeTokenProvider: Provider = {
  provide: REALTIME_TOKEN_PROVIDER,
  useFactory: (): RealtimeTokenProvider => {
    const auth = inject(UserAuthenticationService);
    const sso = inject(ErpSsoService);

    // Read on every call, never cached: a token captured once would be stale by the time the
    // connection needs to come back.
    //
    // LocalStorageManagerService.getTokenFromStorage() returns the literal string '{}' when
    // there is no token, not null - so that placeholder has to be filtered out here or the
    // connection's "no token, don't attempt" guard never fires.
    const storedToken = (): string | null => {
      const token = auth.getToken();
      return token && token !== '{}' ? token : null;
    };

    return {
      getToken: storedToken,
      // The same renewal the HTTP interceptor uses, so a reconnect and a 401 share one refresh call.
      getFreshToken: async () => {
        const token = storedToken();
        if (!token || !expiresSoon(token)) return token;

        try {
          await lastValueFrom(auth.refreshToken(token));
        } catch (error) {
          if (!apiUnreachable(error)) sso.restartSignIn();
          throw error;
        }
        return storedToken();
      }
    };
  }
};
